import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { useApi } from "../../../app/apiContext";
import { ConfirmModal, Skeleton } from "@libs/rubick";
import { NodeDetails } from "./NodeDetails";
import { EditNodeModal } from "./EditNodeModal";
import { useAppSelector } from "../../../app/hooks";
import { selectPagePermissions } from "../../../app/utils/pagePermissionsUtils";
export function NodeDetailsContainer({ nodeId }) {
    const { nodesApi } = useApi();
    const { data: nodeDetails, isLoading, error, refetch } = nodesApi.useGetNodeDetailsQuery(nodeId, {
        pollingInterval: 3000
    });
    const [updateNode, { isLoading: isUpdating }] = nodesApi.useUpdateNodeMutation();
    const [deleteNode, { isLoading: isDeleting }] = nodesApi.useDeleteNodeMutation();
    const { canWriteCluster } = useAppSelector(selectPagePermissions);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [updateError, setUpdateError] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const handleEditClick = ()=>{
        setUpdateError(null);
        setIsEditModalOpen(true);
    };
    const handleEditClose = ()=>{
        setIsEditModalOpen(false);
        setUpdateError(null);
    };
    const handleEditSubmit = async (data)=>{
        try {
            setUpdateError(null);
            await updateNode({
                nodeId,
                description: data.description,
                location: data.location
            }).unwrap();
            // Refetch to get updated data
            await refetch();
            // Show success message
            setSuccessMessage("Node updated successfully");
            setTimeout(()=>setSuccessMessage(null), 3000);
            // Close modal on success
            setIsEditModalOpen(false);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to update node";
            setUpdateError(errorMessage);
        }
    };
    const handleDeleteClick = ()=>{
        setDeleteError(null);
        setIsDeleteConfirmOpen(true);
    };
    const handleDeleteConfirm = async ()=>{
        try {
            setDeleteError(null);
            await deleteNode(nodeId).unwrap();
            setIsDeleteConfirmOpen(false);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to delete node";
            setDeleteError(errorMessage);
        }
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "px-6 pb-6 pt-4 border-t border-gray-200",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-2 gap-4 mb-4",
                    children: [
                        /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-40 rounded-lg"
                        }),
                        /*#__PURE__*/ _jsx(Skeleton, {
                            className: "h-40 rounded-lg"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-60 rounded-lg"
                })
            ]
        });
    }
    if (error || !nodeDetails) {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-6 text-center text-red-500",
            children: "Failed to load node details."
        });
    }
    const node = {
        ...nodeDetails,
        lastCheck: new Date(nodeDetails.lastCheck)
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            successMessage && /*#__PURE__*/ _jsx("div", {
                className: "px-6 pt-4",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "p-3 bg-green-50 border border-green-200 rounded-lg",
                    children: /*#__PURE__*/ _jsx("p", {
                        className: "text-green-700 text-sm",
                        children: successMessage
                    })
                })
            }),
            /*#__PURE__*/ _jsx(NodeDetails, {
                node: node,
                onEditClick: handleEditClick,
                onDeleteClick: handleDeleteClick,
                canDelete: canWriteCluster
            }),
            /*#__PURE__*/ _jsx(EditNodeModal, {
                isOpen: isEditModalOpen,
                onClose: handleEditClose,
                onSubmit: handleEditSubmit,
                initialDescription: node.description,
                initialLocation: node.location,
                isSubmitting: isUpdating,
                error: updateError
            }),
            /*#__PURE__*/ _jsx(ConfirmModal, {
                isOpen: isDeleteConfirmOpen,
                onClose: ()=>setIsDeleteConfirmOpen(false),
                onConfirm: handleDeleteConfirm,
                title: "Remove Node",
                message: "Are you sure you want to remove this node from the cluster? The node will no longer be able to communicate with the control plane and will need to be wiped and re-initialized to be usable again.",
                confirmText: "Remove Node",
                cancelText: "Cancel",
                variant: "danger",
                isLoading: isDeleting,
                error: deleteError
            })
        ]
    });
}
